import { useCallback, useEffect, useRef, useState } from "react";
import {
  createConcurrencyLimiter,
  mapSettledWithConcurrency,
  type AiGrant,
  type AiGrantAccess,
  type AiGrantListItem,
  type DrawingGalleryDetail,
  type DrawingListItem,
  type DrawingSaveResponse,
} from "@excalidraw-ms/shared";
import type { GalleryItemBase, GalleryTab } from "@excalidraw-ms/shared-ui";
import { BackendError } from "../api/backendClient";
import * as backendClient from "../api/backendClient";
import { clearPreviewCache, deletePreview, getPreview } from "../utils/previewCache";

export interface DrawingItem extends GalleryItemBase {
  drawingId: string;
  ownerOid: string;
  role: DrawingListItem["role"];
  storageGeo: DrawingListItem["storageGeo"];
  lastModifiedAt?: string;
  sharedWithPeople?: boolean;
  /** Active AI grant code (design D9). Present only on "Shared with AI" items. */
  aiGrantCode?: string;
  /**
   * What the user can do with the drawing behind the grant — reported, not
   * conferred. Absent when the drawing is unreachable.
   */
  aiGrantAccess?: AiGrantAccess;
  /** When an agent last redeemed this grant, if ever. */
  aiGrantLastUsedAt?: string;
  /** True when the grant points at a drawing that is deleted or unreachable. */
  aiGrantMissingDrawing?: boolean;
  copySourceDrawingId?: string;
  copyRetryable?: boolean;
}

export type GalleryTabId = "recent" | "shared" | "ai";

/**
 * The minimum a drawing must supply to be handed to an assistant.
 *
 * "Share with AI" has two entry points: a gallery card, and the main menu while
 * the drawing is open. The open drawing is not a gallery card — `ownerOid` and
 * `storageGeo` are card-rendering fields that `DrawingLoadResponse` does not
 * carry — and the grant flow reads neither. So the share path asks for exactly
 * what it uses rather than forcing the menu to invent the rest.
 */
export type AiShareTarget = Pick<DrawingItem, "drawingId" | "title" | "role"> &
  Partial<DrawingItem>;

/** True when a share target is a full gallery card and may join the gallery's
 *  own lists. A drawing shared from the main menu is not. */
export function isDrawingItem(target: AiShareTarget): target is DrawingItem {
  return typeof target.ownerOid === "string" && typeof target.storageGeo === "string";
}

interface GalleryCacheEntry {
  recent: DrawingItem[];
  shared: DrawingItem[];
  ai: DrawingItem[];
  totalCount: Record<GalleryTabId, number>;
  continuationToken: Record<GalleryTabId, string | undefined>;
}

const galleryCache = new Map<string, GalleryCacheEntry>();
const GALLERY_PAGE_SIZE = 24;
const PREVIEW_DOWNLOAD_CONCURRENCY = 4;
const previewDownloadLimiter = createConcurrencyLimiter(PREVIEW_DOWNLOAD_CONCURRENCY);

const EMPTY_CACHE_ENTRY = (): GalleryCacheEntry => ({
  recent: [],
  shared: [],
  ai: [],
  totalCount: { recent: 0, shared: 0, ai: 0 },
  continuationToken: { recent: undefined, shared: undefined, ai: undefined },
});

function withoutThumbnail(item: DrawingItem): DrawingItem {
  return { ...item, thumbnailUrl: undefined };
}

function readCachedItems(cacheKey: string | undefined, tab: GalleryTabId): DrawingItem[] {
  if (!cacheKey) return [];
  return galleryCache.get(cacheKey)?.[tab].map(withoutThumbnail) ?? [];
}

function writeCachedItems(
  cacheKey: string | undefined,
  tab: GalleryTabId,
  items: DrawingItem[],
): void {
  if (!cacheKey) return;
  const current = galleryCache.get(cacheKey) ?? EMPTY_CACHE_ENTRY();
  galleryCache.set(cacheKey, {
    ...current,
    [tab]: items.map(withoutThumbnail),
  });
}

function readCachedTotalCount(cacheKey: string | undefined): Record<GalleryTabId, number> {
  return cacheKey
    ? (galleryCache.get(cacheKey)?.totalCount ?? { recent: 0, shared: 0, ai: 0 })
    : { recent: 0, shared: 0, ai: 0 };
}

function readCachedContinuationTokens(
  cacheKey: string | undefined,
): Record<GalleryTabId, string | undefined> {
  return cacheKey
    ? (galleryCache.get(cacheKey)?.continuationToken ?? {
        recent: undefined,
        shared: undefined,
        ai: undefined,
      })
    : { recent: undefined, shared: undefined, ai: undefined };
}

function writeCachedPageState(
  cacheKey: string | undefined,
  tab: GalleryTabId,
  totalCount: number,
  continuationToken: string | undefined,
): void {
  if (!cacheKey) return;
  const current = galleryCache.get(cacheKey) ?? EMPTY_CACHE_ENTRY();
  galleryCache.set(cacheKey, {
    ...current,
    totalCount: { ...current.totalCount, [tab]: totalCount },
    continuationToken: { ...current.continuationToken, [tab]: continuationToken },
  });
}

export function clearGalleryCache(cacheKey?: string): void {
  if (cacheKey) galleryCache.delete(cacheKey);
  else galleryCache.clear();
  // Preview bytes are customer content; drop them whenever the gallery cache is
  // dropped so they cannot outlive the signed-in session on a shared device.
  clearPreviewCache();
}

export function toDrawingItem(
  drawing: DrawingListItem,
  detail?: DrawingGalleryDetail,
  thumbnailUrl?: string,
): DrawingItem {
  return {
    id: drawing.drawingId,
    title: detail?.name ?? `Drawing ${drawing.drawingId.slice(0, 8)}`,
    thumbnailUrl,
    drawingId: drawing.drawingId,
    ownerOid: drawing.ownerOid,
    role: drawing.role,
    storageGeo: drawing.storageGeo,
    lastModifiedAt: drawing.updatedAt,
    sharedWithPeople: drawing.role === "owner" ? detail?.sharedWithPeople : true,
  };
}

/**
 * Map an AI grant (design D9) to a gallery item for the "Shared with AI" tab.
 * The grant's `drawing` may be null when the drawing was deleted or is no
 * longer reachable — such rows still render (so the user can revoke the dead
 * grant), just with a placeholder title and no thumbnail.
 */
export function grantToDrawingItem(grant: AiGrantListItem): DrawingItem {
  const drawing = grant.drawing;
  return {
    id: grant.drawingId,
    title: drawing
      ? `Drawing ${grant.drawingId.slice(0, 8)}`
      : `Unavailable drawing ${grant.drawingId.slice(0, 8)}`,
    thumbnailUrl: undefined,
    drawingId: grant.drawingId,
    ownerOid: drawing?.ownerOid ?? "",
    role: drawing?.role ?? grant.access?.role ?? "viewer",
    storageGeo: drawing?.storageGeo ?? "NAM",
    lastModifiedAt: drawing?.updatedAt,
    sharedWithPeople: drawing && drawing.role !== "owner" ? true : undefined,
    aiGrantCode: grant.code,
    aiGrantAccess: grant.access ?? undefined,
    aiGrantLastUsedAt: grant.lastUsedAt,
    aiGrantMissingDrawing: !drawing,
  };
}

export function formatRelativeDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return "";
  }
}

export function friendlyErrorMessage(err: unknown): string {
  if (err instanceof BackendError) {
    if (err.status === 401 || err.status === 403) {
      return "Your Excalidraw-MS access may have changed. Sign in again or check your group membership.";
    }
    if (err.status === 502 || err.status === 503 || err.status === 504) {
      return "The service is temporarily unavailable. Please try again in a moment.";
    }
    if (err.status >= 500) return "Something went wrong on the server. Please try again later.";
    return `Unable to load drawings (${err.status}).`;
  }
  if (err instanceof TypeError && err.message.includes("fetch")) {
    return "Unable to connect to the server. Check your network connection.";
  }
  return "Failed to load drawings. Please try again.";
}

export function buildGalleryTabs(
  ownedCount: number,
  sharedCount: number,
  aiCount: number,
): GalleryTab[] {
  return [
    {
      id: "recent",
      label: "My Drawings",
      count: ownedCount || undefined,
      emptyState: {
        title: "No drawings yet",
        description: "Save a drawing and it will appear here.",
      },
    },
    {
      id: "shared",
      label: "Shared with Me",
      count: sharedCount || undefined,
      emptyState: {
        title: "No shared drawings",
        description: "Drawings shared with you will appear here after you redeem their links.",
      },
    },
    {
      id: "ai",
      label: "Shared with AI",
      count: aiCount || undefined,
      emptyState: {
        title: "No drawings shared with AI",
        description: "Use Share on a drawing, then Share with AI to get a code for your assistant.",
      },
    },
  ];
}

export function useGalleryData(
  getToken: () => Promise<string>,
  enabled: boolean,
  cacheKey?: string,
) {
  const [activeTab, setActiveTab] = useState<GalleryTabId>("recent");
  const [ownedItems, setOwnedItems] = useState<DrawingItem[]>(() =>
    readCachedItems(cacheKey, "recent"),
  );
  const [sharedItems, setSharedItems] = useState<DrawingItem[]>(() =>
    readCachedItems(cacheKey, "shared"),
  );
  const [aiItems, setAiItems] = useState<DrawingItem[]>(() => readCachedItems(cacheKey, "ai"));
  const [copies, setCopies] = useState<DrawingItem[]>([]);
  const copiesRef = useRef(copies);
  copiesRef.current = copies;
  const deletedDrawingIdsRef = useRef(new Set<string>());
  const persistedCopyMetadataRef = useRef(new Map<string, { name: string; updatedAt: string }>());
  const unconfirmedCopyPeopleStatusRef = useRef(new Map<string, boolean>());
  const updateCopies = (update: (current: DrawingItem[]) => DrawingItem[]) => {
    const next = update(copiesRef.current);
    copiesRef.current = next;
    setCopies(next);
  };
  const recordCopyMetadata = (id: string, result: DrawingSaveResponse) => {
    if (result.name) {
      persistedCopyMetadataRef.current.set(id, {
        name: result.name,
        updatedAt: result.updatedAt,
      });
    }
    const update = (item: DrawingItem): DrawingItem =>
      item.drawingId === id
        ? {
            ...item,
            title: result.name ?? item.title,
            lastModifiedAt: result.updatedAt,
          }
        : item;
    updateCopies((current) => current.map(update));
    setOwnedItems((current) => {
      if (!current.some((item) => item.drawingId === id)) return current;
      const next = current.map(update);
      writeCachedItems(cacheKey, "recent", next);
      return next;
    });
  };
  const ownedItemsRef = useRef(ownedItems);
  const sharedItemsRef = useRef(sharedItems);
  const aiItemsRef = useRef(aiItems);
  ownedItemsRef.current = ownedItems;
  sharedItemsRef.current = sharedItems;
  aiItemsRef.current = aiItems;
  const [loadingByTab, setLoadingByTab] = useState<Record<GalleryTabId, boolean>>({
    recent: false,
    shared: false,
    ai: false,
  });
  const [loadingMoreByTab, setLoadingMoreByTab] = useState<Record<GalleryTabId, boolean>>({
    recent: false,
    shared: false,
    ai: false,
  });
  const [totalCountByTab, setTotalCountByTab] = useState<Record<GalleryTabId, number>>(() =>
    readCachedTotalCount(cacheKey),
  );
  const totalCountByTabRef = useRef(totalCountByTab);
  totalCountByTabRef.current = totalCountByTab;
  const [errorByTab, setErrorByTab] = useState<Record<GalleryTabId, string | null>>({
    recent: null,
    shared: null,
    ai: null,
  });
  const [sharedIndexRevision, setSharedIndexRevision] = useState(0);
  const [aiIndexRevision, setAiIndexRevision] = useState(0);
  const requestIdRef = useRef<Record<GalleryTabId, number>>({
    recent: 0,
    shared: 0,
    ai: 0,
  });
  const listedDrawingsByTabRef = useRef<Record<GalleryTabId, readonly DrawingListItem[] | null>>({
    recent: null,
    shared: null,
    ai: null,
  });
  const sharedHydratedRevisionRef = useRef(0);
  const sharedHydrationInFlightRevisionRef = useRef(0);
  const aiHydratedRevisionRef = useRef(0);
  const aiHydrationInFlightRevisionRef = useRef(0);
  const continuationTokenByTabRef = useRef<Record<GalleryTabId, string | undefined>>(
    readCachedContinuationTokens(cacheKey),
  );
  const pageRequestInFlightByTabRef = useRef<Record<GalleryTabId, boolean>>({
    recent: false,
    shared: false,
    ai: false,
  });
  const authTokenByTabRef = useRef<Record<GalleryTabId, string | undefined>>({
    recent: undefined,
    shared: undefined,
    ai: undefined,
  });
  const visibleDrawingIdsByTabRef = useRef<Record<GalleryTabId, Set<string>>>({
    recent: new Set(),
    shared: new Set(),
    ai: new Set(),
  });
  const requestedPreviewIdsByTabRef = useRef<Record<GalleryTabId, Set<string>>>({
    recent: new Set(),
    shared: new Set(),
    ai: new Set(),
  });
  const detailsByTabRef = useRef<Record<GalleryTabId, Map<string, DrawingGalleryDetail>>>({
    recent: new Map(),
    shared: new Map(),
    ai: new Map(),
  });
  const [grantMetadataByDrawingId, setGrantMetadataByDrawingId] = useState(
    () => new Map<string, { code: string; access: AiGrantAccess | null }>(),
  );
  const grantMetadataByDrawingIdRef = useRef(grantMetadataByDrawingId);
  grantMetadataByDrawingIdRef.current = grantMetadataByDrawingId;
  const [aiStatusKnownDrawingIds, setAiStatusKnownDrawingIds] = useState(() => new Set<string>());
  const sharingRevisionRef = useRef(0);
  const peopleMutationRef = useRef(new Map<string, { revision: number; isShared: boolean }>());
  const aiMutationRef = useRef(
    new Map<
      string,
      {
        revision: number;
        grant: { code: string; access: AiGrantAccess | null; lastUsedAt?: string } | null;
      }
    >(),
  );
  const retrySharingStatusRef = useRef(new Set<GalleryTabId>());
  const retryWithoutAppendRef = useRef(new Set<GalleryTabId>());
  const previewUrlsByTabRef = useRef<Record<GalleryTabId, Map<string, string>>>({
    recent: new Map(),
    shared: new Map(),
    ai: new Map(),
  });
  const previewStampsByTabRef = useRef<Record<GalleryTabId, Map<string, string>>>({
    recent: new Map(),
    shared: new Map(),
    ai: new Map(),
  });

  const setPreviewUrl = useCallback(
    (tab: GalleryTabId, drawingId: string, url: string, stamp: string) => {
      previewUrlsByTabRef.current[tab].set(drawingId, url);
      previewStampsByTabRef.current[tab].set(drawingId, stamp);
    },
    [],
  );

  /**
   * Keep previews for drawings re-listed with the same `updatedAt` stamp and
   * revoke the rest, so a re-list only downloads new or changed thumbnails.
   */
  const retainUnchangedPreviews = useCallback(
    (tab: GalleryTabId, listed: readonly { drawingId: string; stamp: string | undefined }[]) => {
      const stampById = new Map(listed.map((entry) => [entry.drawingId, entry.stamp]));
      const urls = previewUrlsByTabRef.current[tab];
      const stamps = previewStampsByTabRef.current[tab];
      const retained = new Map<string, string>();
      urls.forEach((url, drawingId) => {
        const stamp = stampById.get(drawingId);
        if (stamp !== undefined && stamps.get(drawingId) === stamp) retained.set(drawingId, url);
        else URL.revokeObjectURL(url);
      });
      urls.clear();
      stamps.clear();
      requestedPreviewIdsByTabRef.current[tab].clear();
      retained.forEach((url, drawingId) => {
        setPreviewUrl(tab, drawingId, url, stampById.get(drawingId)!);
        requestedPreviewIdsByTabRef.current[tab].add(drawingId);
      });
      return retained;
    },
    [setPreviewUrl],
  );

  const queuePreviewDownloads = useCallback(
    (
      tab: GalleryTabId,
      authToken: string,
      drawingsToPreview: readonly DrawingListItem[],
      detailById: Map<string, DrawingGalleryDetail>,
    ) => {
      detailById.forEach((detail, drawingId) =>
        detailsByTabRef.current[tab].set(drawingId, detail),
      );
      const requestId = requestIdRef.current[tab];
      const candidates = drawingsToPreview.filter((drawing) => {
        if (!visibleDrawingIdsByTabRef.current[tab].has(drawing.drawingId)) return false;
        if (requestedPreviewIdsByTabRef.current[tab].has(drawing.drawingId)) return false;
        if (!detailById.get(drawing.drawingId)?.previewUrl) return false;
        requestedPreviewIdsByTabRef.current[tab].add(drawing.drawingId);
        return true;
      });
      if (candidates.length === 0) return;
      void mapSettledWithConcurrency(candidates, PREVIEW_DOWNLOAD_CONCURRENCY, async (drawing) => {
        try {
          const preview = await previewDownloadLimiter.run(async () => {
            if (!enabled || requestId !== requestIdRef.current[tab]) return undefined;
            return backendClient.getDrawingPreview(authToken, drawing.drawingId, drawing.updatedAt);
          });
          if (!preview) return undefined;
          const previewUrl = URL.createObjectURL(preview);
          if (!enabled || requestId !== requestIdRef.current[tab]) {
            URL.revokeObjectURL(previewUrl);
            return undefined;
          }
          const previousUrl = previewUrlsByTabRef.current[tab].get(drawing.drawingId);
          if (previousUrl) URL.revokeObjectURL(previousUrl);
          setPreviewUrl(tab, drawing.drawingId, previewUrl, drawing.updatedAt);
          const update = (items: DrawingItem[]) =>
            items.map((item) =>
              item.drawingId === drawing.drawingId ? { ...item, thumbnailUrl: previewUrl } : item,
            );
          if (tab === "recent") setOwnedItems(update);
          else if (tab === "shared") setSharedItems(update);
          else setAiItems(update);
          return previewUrl;
        } catch {
          return undefined;
        }
      });
    },
    [enabled, setPreviewUrl],
  );

  const fetchTab = useCallback(
    async (
      tab: GalleryTabId,
      hydrateDetails = true,
      reuseListedDrawings = false,
      append = false,
    ) => {
      if (!enabled) return false;
      if (!append && !reuseListedDrawings) {
        retrySharingStatusRef.current.delete(tab);
        retryWithoutAppendRef.current.delete(tab);
      }
      const sharingRevision = sharingRevisionRef.current;
      const peopleStatus = (item: DrawingItem, detail: DrawingGalleryDetail) => {
        if (item.role !== "owner") return true;
        const newerStatus = peopleMutationRef.current.get(item.drawingId);
        if (newerStatus && newerStatus.revision > sharingRevision) return newerStatus.isShared;
        if (detail.sharedWithPeople !== undefined) return detail.sharedWithPeople;
        return unconfirmedCopyPeopleStatusRef.current.get(item.drawingId);
      };
      if (append && pageRequestInFlightByTabRef.current[tab]) return false;
      if (append) pageRequestInFlightByTabRef.current[tab] = true;
      const requestId = append ? requestIdRef.current[tab] : ++requestIdRef.current[tab];
      const isStale = () => requestId !== requestIdRef.current[tab];
      const applyTabItems = (
        value: DrawingItem[] | ((current: DrawingItem[]) => DrawingItem[]),
      ) => {
        if (tab === "recent") setOwnedItems(value);
        else if (tab === "shared") setSharedItems(value);
        else setAiItems(value);
      };
      const setTabItems = (items: DrawingItem[]) => {
        applyTabItems(items);
        writeCachedItems(cacheKey, tab, items);
      };
      const setTabLoading = (loading: boolean) => {
        setLoadingByTab((current) => ({ ...current, [tab]: loading }));
      };
      const setTabError = (error: string | null) => {
        setErrorByTab((current) => ({ ...current, [tab]: error }));
      };

      if (append) setLoadingMoreByTab((current) => ({ ...current, [tab]: true }));
      else
        setTabLoading(
          readCachedItems(cacheKey, tab).length === 0 &&
            !(tab === "recent" && copiesRef.current.length > 0),
        );
      if (!reuseListedDrawings || !retrySharingStatusRef.current.has(tab)) {
        setTabError(null);
      }
      try {
        const token = await getToken();
        authTokenByTabRef.current[tab] = token;
        if (isStale()) return false;

        // "Shared with AI" is a genuinely separate collection (design D9.4):
        // its rows come from the AI grant list, not from filtering the other
        // tabs, and a grant may point at a null (deleted) drawing.
        if (tab === "ai") {
          let grantDrawings = listedDrawingsByTabRef.current.ai ?? [];
          if (!reuseListedDrawings) {
            const response = await backendClient.listAiGrants(
              token,
              GALLERY_PAGE_SIZE,
              append ? continuationTokenByTabRef.current.ai : undefined,
            );
            if (isStale()) return false;
            const hasNewerAiStatus = [...aiMutationRef.current.values()].some(
              (status) => status.revision > sharingRevision,
            );
            continuationTokenByTabRef.current.ai = response.continuationToken;
            setTotalCountByTab((current) => {
              if (hasNewerAiStatus) return current;
              const nextCount =
                (response.totalCount ?? current.ai) -
                Math.min(response.staleGrantCount ?? 0, response.totalCount ?? current.ai);
              writeCachedPageState(cacheKey, "ai", nextCount, response.continuationToken);
              return { ...current, ai: nextCount };
            });
            const grants = response.grants.filter(
              (grant) =>
                (aiMutationRef.current.get(grant.drawingId)?.revision ?? 0) <= sharingRevision,
            );
            setGrantMetadataByDrawingId((current) => {
              const next = new Map(current);
              grants.forEach((grant) =>
                next.set(grant.drawingId, { code: grant.code, access: grant.access }),
              );
              return next;
            });
            const pageDrawings = grants
              .map((grant) => grant.drawing)
              .filter((drawing): drawing is DrawingListItem => Boolean(drawing));
            grantDrawings = append
              ? [
                  ...grantDrawings,
                  ...pageDrawings.filter(
                    (drawing) =>
                      !grantDrawings.some((existing) => existing.drawingId === drawing.drawingId),
                  ),
                ]
              : pageDrawings;
            listedDrawingsByTabRef.current.ai = grantDrawings;
            const cachedById = new Map(
              readCachedItems(cacheKey, "ai").map((item) => [item.drawingId, item]),
            );
            const retainedPreviews = append
              ? new Map<string, string>()
              : retainUnchangedPreviews(
                  "ai",
                  grants.map((grant) => ({
                    drawingId: grant.drawingId,
                    stamp: grant.drawing?.updatedAt,
                  })),
                );
            if (!append) detailsByTabRef.current.ai.clear();
            const pageItems = grants.map((grant) => {
              const item = grantToDrawingItem(grant);
              let thumbnailUrl = retainedPreviews.get(item.drawingId);
              const cachedPreview =
                !thumbnailUrl && grant.drawing
                  ? getPreview(item.drawingId, grant.drawing.updatedAt)
                  : undefined;
              if (cachedPreview && grant.drawing) {
                thumbnailUrl = URL.createObjectURL(cachedPreview);
                setPreviewUrl("ai", item.drawingId, thumbnailUrl, grant.drawing.updatedAt);
              }
              return {
                ...item,
                title: cachedById.get(item.drawingId)?.title ?? item.title,
                thumbnailUrl,
              };
            });
            if (append) {
              applyTabItems((current) => {
                const existingIds = new Set(current.map((item) => item.id));
                const next = [...current, ...pageItems.filter((item) => !existingIds.has(item.id))];
                writeCachedItems(cacheKey, tab, next);
                return next;
              });
            } else {
              applyTabItems((current) => {
                const protectedItems = current.filter(
                  (item) =>
                    (aiMutationRef.current.get(item.drawingId)?.revision ?? 0) > sharingRevision,
                );
                const protectedIds = new Set(protectedItems.map((item) => item.drawingId));
                const next = [
                  ...protectedItems,
                  ...pageItems.filter((item) => !protectedIds.has(item.drawingId)),
                ];
                writeCachedItems(cacheKey, tab, next);
                return next;
              });
            }
            if (!isStale()) setTabLoading(false);
            if (!hydrateDetails) {
              setAiIndexRevision((current) => current + 1);
              return false;
            }
            grantDrawings = pageDrawings;
          }
          if (grantDrawings.length === 0) return true;
          let detailsResponse: Awaited<ReturnType<typeof backendClient.getDrawingGalleryDetails>>;
          try {
            detailsResponse = await backendClient.getDrawingGalleryDetails(
              token,
              grantDrawings.map((drawing) => drawing.drawingId),
            );
          } catch (error) {
            retryWithoutAppendRef.current.add(tab);
            throw error;
          }
          if (isStale()) return false;
          const detailById = new Map(
            detailsResponse.drawings.map((detail) => [detail.drawingId, detail]),
          );
          applyTabItems((current) => {
            const next = current.map((item) => {
              const detail = detailById.get(item.drawingId);
              return detail
                ? {
                    ...item,
                    title: detail.name,
                    sharedWithPeople: peopleStatus(item, detail),
                  }
                : item;
            });
            writeCachedItems(cacheKey, tab, next);
            return next;
          });
          if (!isStale()) setTabLoading(false);
          queuePreviewDownloads(tab, token, grantDrawings, detailById);
          return true;
        }

        let drawings = listedDrawingsByTabRef.current[tab];
        let pageDrawings = drawings ?? [];
        if (!reuseListedDrawings) {
          const continuationToken = append ? continuationTokenByTabRef.current[tab] : undefined;
          const deletedDrawingIds = [...deletedDrawingIdsRef.current];
          const response =
            tab === "recent"
              ? deletedDrawingIds.length > 0
                ? await backendClient.listOwnedDrawings(
                    token,
                    GALLERY_PAGE_SIZE,
                    continuationToken,
                    deletedDrawingIds,
                  )
                : await backendClient.listOwnedDrawings(token, GALLERY_PAGE_SIZE, continuationToken)
              : await backendClient.listSharedDrawings(token, GALLERY_PAGE_SIZE, continuationToken);
          if (isStale()) return false;
          pageDrawings =
            tab === "recent"
              ? response.drawings.filter(
                  (drawing) => !deletedDrawingIdsRef.current.has(drawing.drawingId),
                )
              : response.drawings;
          drawings = append
            ? [
                ...(drawings ?? []),
                ...pageDrawings.filter(
                  (drawing) =>
                    !(drawings ?? []).some((existing) => existing.drawingId === drawing.drawingId),
                ),
              ]
            : pageDrawings;
          listedDrawingsByTabRef.current[tab] = drawings;
          continuationTokenByTabRef.current[tab] = response.continuationToken;
          if (response.totalCount !== undefined) {
            setTotalCountByTab((current) => {
              const next = { ...current, [tab]: response.totalCount! };
              totalCountByTabRef.current = next;
              writeCachedPageState(cacheKey, tab, response.totalCount!, response.continuationToken);
              return next;
            });
          } else {
            setTotalCountByTab((current) => {
              writeCachedPageState(cacheKey, tab, current[tab], response.continuationToken);
              return current;
            });
          }
          const cachedById = new Map(
            [...readCachedItems(cacheKey, tab), ...(tab === "recent" ? copiesRef.current : [])].map(
              (item) => [item.drawingId, item],
            ),
          );
          const retainedPreviews = append
            ? new Map<string, string>()
            : retainUnchangedPreviews(
                tab,
                pageDrawings.map((drawing) => ({
                  drawingId: drawing.drawingId,
                  stamp: drawing.updatedAt,
                })),
              );
          if (!append) detailsByTabRef.current[tab].clear();
          // Seed cards from previews this tab produced at save time. SharePoint
          // renders thumbnail derivatives asynchronously, so a drawing just
          // saved here would otherwise show its previous image. The cache only
          // answers when its stamp matches this list exactly, so an edit made
          // elsewhere can never be masked by a local image.
          const listedItems = pageDrawings.map((drawing) => {
            const listedItem = toDrawingItem(drawing);
            const persistedCopy = persistedCopyMetadataRef.current.get(drawing.drawingId);
            const newerPeopleStatus = peopleMutationRef.current.get(drawing.drawingId);
            const confirmedCopyStatus = unconfirmedCopyPeopleStatusRef.current.get(
              drawing.drawingId,
            );
            let thumbnailUrl = retainedPreviews.get(drawing.drawingId);
            const cachedPreview = thumbnailUrl
              ? undefined
              : getPreview(drawing.drawingId, drawing.updatedAt);
            if (cachedPreview) {
              thumbnailUrl = URL.createObjectURL(cachedPreview);
              setPreviewUrl(tab, drawing.drawingId, thumbnailUrl, drawing.updatedAt);
            }
            return {
              ...listedItem,
              title:
                persistedCopy?.name ?? cachedById.get(drawing.drawingId)?.title ?? listedItem.title,
              lastModifiedAt:
                persistedCopy &&
                Date.parse(persistedCopy.updatedAt) > Date.parse(listedItem.lastModifiedAt ?? "")
                  ? persistedCopy.updatedAt
                  : listedItem.lastModifiedAt,
              thumbnailUrl,
              sharedWithPeople:
                newerPeopleStatus && newerPeopleStatus.revision > sharingRevision
                  ? newerPeopleStatus.isShared
                  : (confirmedCopyStatus ?? listedItem.sharedWithPeople),
            };
          });
          if (append) {
            applyTabItems((current) => {
              const existingIds = new Set(current.map((item) => item.id));
              const next = [...current, ...listedItems.filter((item) => !existingIds.has(item.id))];
              writeCachedItems(cacheKey, tab, next);
              return next;
            });
          } else {
            setTabItems(listedItems);
          }
          if (tab === "recent") {
            const listedIds = new Set(pageDrawings.map((drawing) => drawing.drawingId));
            if (
              copiesRef.current.some((copy) => !copy.cardStatus && listedIds.has(copy.drawingId))
            ) {
              updateCopies((current) =>
                current.filter((copy) => copy.cardStatus || !listedIds.has(copy.drawingId)),
              );
            }
          }
          if (!isStale()) setTabLoading(false);
          void backendClient
            .lookupAiGrants(
              token,
              pageDrawings.map((drawing) => drawing.drawingId),
            )
            .then((grantLookup) => {
              if (isStale()) return;
              setAiStatusKnownDrawingIds(
                (current) =>
                  new Set([...current, ...pageDrawings.map((drawing) => drawing.drawingId)]),
              );
              setGrantMetadataByDrawingId((current) => {
                const next = new Map(current);
                pageDrawings.forEach((drawing) => {
                  if (
                    (aiMutationRef.current.get(drawing.drawingId)?.revision ?? 0) <= sharingRevision
                  ) {
                    next.delete(drawing.drawingId);
                  }
                });
                grantLookup.grants.forEach((grant) => {
                  if (
                    (aiMutationRef.current.get(grant.drawingId)?.revision ?? 0) > sharingRevision
                  ) {
                    return;
                  }
                  next.set(grant.drawingId, {
                    code: grant.code,
                    access: grant.access,
                  });
                });
                return next;
              });
              const grantByDrawingId = new Map(
                grantLookup.grants.map((grant) => [grant.drawingId, grant]),
              );
              const updateProjection = (tab: GalleryTabId) => (items: DrawingItem[]) => {
                const next = items.map((item) => {
                  if (!pageDrawings.some((drawing) => drawing.drawingId === item.drawingId)) {
                    return item;
                  }
                  if (
                    (aiMutationRef.current.get(item.drawingId)?.revision ?? 0) > sharingRevision
                  ) {
                    return item;
                  }
                  const grant = grantByDrawingId.get(item.drawingId);
                  return grant
                    ? {
                        ...item,
                        aiGrantCode: grant.code,
                        aiGrantAccess: grant.access ?? undefined,
                        aiGrantLastUsedAt: grant.lastUsedAt,
                        aiGrantMissingDrawing: !grant.drawing,
                      }
                    : {
                        ...item,
                        aiGrantCode: undefined,
                        aiGrantAccess: undefined,
                        aiGrantLastUsedAt: undefined,
                        aiGrantMissingDrawing: undefined,
                      };
                });
                writeCachedItems(cacheKey, tab, next);
                return next;
              };
              setOwnedItems(updateProjection("recent"));
              setSharedItems(updateProjection("shared"));
              const checkedIds = new Set(pageDrawings.map((drawing) => drawing.drawingId));
              const removedAiIds = new Set(
                [...checkedIds].filter(
                  (drawingId) =>
                    !grantByDrawingId.has(drawingId) &&
                    (aiMutationRef.current.get(drawingId)?.revision ?? 0) <= sharingRevision &&
                    (grantMetadataByDrawingIdRef.current.has(drawingId) ||
                      aiItemsRef.current.some((item) => item.drawingId === drawingId)),
                ),
              );
              if (removedAiIds.size > 0) {
                setAiItems((items) => {
                  const next = items.filter((item) => !removedAiIds.has(item.drawingId));
                  writeCachedItems(cacheKey, "ai", next);
                  return next;
                });
                setTotalCountByTab((counts) => {
                  const ai = Math.max(0, counts.ai - removedAiIds.size);
                  writeCachedPageState(cacheKey, "ai", ai, continuationTokenByTabRef.current.ai);
                  return { ...counts, ai };
                });
              }
            })
            .catch((error: unknown) => {
              console.error("[useGalleryData] AI sharing status lookup failed:", error);
              if (!isStale()) {
                retrySharingStatusRef.current.add(tab);
                setTabError("AI sharing status could not be checked. Please try again.");
              }
            });
          if (!hydrateDetails) {
            if (tab === "shared") setSharedIndexRevision((current) => current + 1);
            return false;
          }
        }
        if (!drawings) return false;
        if (pageDrawings.length === 0) return true;

        let detailsResponse: Awaited<ReturnType<typeof backendClient.getDrawingGalleryDetails>>;
        try {
          detailsResponse = await backendClient.getDrawingGalleryDetails(
            token,
            pageDrawings.map((drawing) => drawing.drawingId),
          );
        } catch (error) {
          retryWithoutAppendRef.current.add(tab);
          throw error;
        }
        if (isStale()) return false;
        const detailById = new Map(
          detailsResponse.drawings.map((detail) => [detail.drawingId, detail]),
        );
        applyTabItems((current) => {
          const next = current.map((item) => {
            const detail = detailById.get(item.drawingId);
            const persistedCopy = persistedCopyMetadataRef.current.get(item.drawingId);
            return detail
              ? {
                  ...item,
                  title:
                    persistedCopy && detail.name === `Drawing ${item.drawingId.slice(0, 8)}`
                      ? persistedCopy.name
                      : detail.name,
                  sharedWithPeople: peopleStatus(item, detail),
                }
              : item;
          });
          writeCachedItems(cacheKey, tab, next);
          return next;
        });
        if (tab === "recent") {
          for (const detail of detailsResponse.drawings) {
            if (
              detail.sharedWithPeople !== undefined &&
              (peopleMutationRef.current.get(detail.drawingId)?.revision ?? 0) <= sharingRevision
            ) {
              unconfirmedCopyPeopleStatusRef.current.delete(detail.drawingId);
            }
          }
        }
        if (!isStale()) setTabLoading(false);

        queuePreviewDownloads(tab, token, pageDrawings, detailById);
        return true;
      } catch (err) {
        if (!isStale()) setTabError(friendlyErrorMessage(err));
        return false;
      } finally {
        if (!isStale()) setTabLoading(false);
        if (!isStale()) {
          setLoadingMoreByTab((current) => ({ ...current, [tab]: false }));
        }
        if (append) pageRequestInFlightByTabRef.current[tab] = false;
      }
    },
    [cacheKey, enabled, getToken, queuePreviewDownloads, retainUnchangedPreviews, setPreviewUrl],
  );

  useEffect(() => {
    setAiStatusKnownDrawingIds(new Set());
    sharingRevisionRef.current = 0;
    peopleMutationRef.current.clear();
    aiMutationRef.current.clear();
    retrySharingStatusRef.current.clear();
    retryWithoutAppendRef.current.clear();
    requestIdRef.current.recent += 1;
    requestIdRef.current.shared += 1;
    requestIdRef.current.ai += 1;
    listedDrawingsByTabRef.current = { recent: null, shared: null, ai: null };
    sharedHydratedRevisionRef.current = 0;
    sharedHydrationInFlightRevisionRef.current = 0;
    aiHydratedRevisionRef.current = 0;
    aiHydrationInFlightRevisionRef.current = 0;
    continuationTokenByTabRef.current = readCachedContinuationTokens(cacheKey);
    pageRequestInFlightByTabRef.current = { recent: false, shared: false, ai: false };
    authTokenByTabRef.current = { recent: undefined, shared: undefined, ai: undefined };
    visibleDrawingIdsByTabRef.current = {
      recent: new Set(),
      shared: new Set(),
      ai: new Set(),
    };
    requestedPreviewIdsByTabRef.current = {
      recent: new Set(),
      shared: new Set(),
      ai: new Set(),
    };
    detailsByTabRef.current = { recent: new Map(), shared: new Map(), ai: new Map() };
    Object.values(previewUrlsByTabRef.current)
      .flatMap((previewUrls) => [...previewUrls.values()])
      .forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    previewUrlsByTabRef.current = { recent: new Map(), shared: new Map(), ai: new Map() };
    previewStampsByTabRef.current = { recent: new Map(), shared: new Map(), ai: new Map() };
    setSharedIndexRevision(0);
    setAiIndexRevision(0);
    setTotalCountByTab(readCachedTotalCount(cacheKey));
    setGrantMetadataByDrawingId(new Map());
    setOwnedItems(readCachedItems(cacheKey, "recent"));
    setSharedItems(readCachedItems(cacheKey, "shared"));
    setAiItems(readCachedItems(cacheKey, "ai"));
    copiesRef.current = [];
    deletedDrawingIdsRef.current.clear();
    persistedCopyMetadataRef.current.clear();
    unconfirmedCopyPeopleStatusRef.current.clear();
    setCopies([]);
  }, [cacheKey]);

  useEffect(() => {
    if (enabled) return;
    requestIdRef.current.recent += 1;
    requestIdRef.current.shared += 1;
    requestIdRef.current.ai += 1;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    void fetchTab("recent");
    void fetchTab("shared", false);
    void fetchTab("ai", false);
  }, [enabled, fetchTab]);

  useEffect(() => {
    if (
      enabled &&
      activeTab === "shared" &&
      sharedIndexRevision > 0 &&
      sharedHydratedRevisionRef.current < sharedIndexRevision &&
      sharedHydrationInFlightRevisionRef.current !== sharedIndexRevision &&
      listedDrawingsByTabRef.current.shared
    ) {
      const revision = sharedIndexRevision;
      sharedHydrationInFlightRevisionRef.current = revision;
      void fetchTab("shared", true, true).then((hydrated) => {
        if (hydrated) sharedHydratedRevisionRef.current = revision;
        if (sharedHydrationInFlightRevisionRef.current === revision) {
          sharedHydrationInFlightRevisionRef.current = 0;
        }
      });
    }
  }, [activeTab, enabled, fetchTab, sharedIndexRevision]);

  useEffect(() => {
    if (
      enabled &&
      activeTab === "ai" &&
      aiIndexRevision > 0 &&
      aiHydratedRevisionRef.current < aiIndexRevision &&
      aiHydrationInFlightRevisionRef.current !== aiIndexRevision &&
      listedDrawingsByTabRef.current.ai
    ) {
      const revision = aiIndexRevision;
      aiHydrationInFlightRevisionRef.current = revision;
      void fetchTab("ai", true, true).then((hydrated) => {
        if (hydrated) aiHydratedRevisionRef.current = revision;
        if (aiHydrationInFlightRevisionRef.current === revision) {
          aiHydrationInFlightRevisionRef.current = 0;
        }
      });
    }
  }, [activeTab, aiIndexRevision, enabled, fetchTab]);

  useEffect(
    () => () => {
      requestIdRef.current.recent += 1;
      requestIdRef.current.shared += 1;
      requestIdRef.current.ai += 1;
      Object.values(previewUrlsByTabRef.current)
        .flatMap((previewUrls) => [...previewUrls.values()])
        .forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    },
    [],
  );

  const itemsByTab: Record<GalleryTabId, DrawingItem[]> = {
    recent: [
      ...copies.filter(
        (copy) => copy.cardStatus || !ownedItems.some((item) => item.drawingId === copy.drawingId),
      ),
      ...ownedItems.filter(
        (item) => !copies.some((copy) => copy.drawingId === item.drawingId && copy.cardStatus),
      ),
    ],
    shared: sharedItems,
    ai: aiItems,
  };
  const markItemVisible = useCallback(
    (item: DrawingItem) => {
      if (item.cardStatus || deletedDrawingIdsRef.current.has(item.drawingId)) return;
      const tab = activeTab;
      visibleDrawingIdsByTabRef.current[tab].add(item.drawingId);
      const token = authTokenByTabRef.current[tab];
      const drawing = listedDrawingsByTabRef.current[tab]?.find(
        (listed) => listed.drawingId === item.drawingId,
      );
      const detail = detailsByTabRef.current[tab].get(item.drawingId);
      if (!token || !drawing || !detail) return;
      queuePreviewDownloads(tab, token, [drawing], new Map([[item.drawingId, detail]]));
    },
    [activeTab, queuePreviewDownloads],
  );
  const loadMore = useCallback(() => {
    if (
      !continuationTokenByTabRef.current[activeTab] ||
      loadingMoreByTab[activeTab] ||
      loadingByTab[activeTab]
    ) {
      return;
    }
    void fetchTab(activeTab, true, false, true);
  }, [activeTab, fetchTab, loadingByTab, loadingMoreByTab]);

  return {
    activeTab,
    setActiveTab: (tab: string) => setActiveTab(tab as GalleryTabId),
    items: itemsByTab[activeTab],
    loading: loadingByTab[activeTab],
    loadingMore: loadingMoreByTab[activeTab],
    hasMore: Boolean(continuationTokenByTabRef.current[activeTab]),
    loadMore,
    onItemVisible: markItemVisible,
    error: errorByTab[activeTab],
    startCopy: (source: DrawingItem): string => {
      const id = crypto.randomUUID();
      let title = "Copy of ";
      for (const character of source.title) {
        if (title.length + character.length > 120) break;
        title += character;
      }
      updateCopies((current) => [
        {
          id,
          drawingId: id,
          title,
          ownerOid: source.ownerOid,
          storageGeo: source.storageGeo,
          role: "owner",
          copySourceDrawingId: source.drawingId,
          cardStatus: { kind: "pending", message: "Making a copy…" },
        },
        ...current,
      ]);
      return id;
    },
    retryCopy: (id: string) => {
      updateCopies((current) =>
        current.map((copy) =>
          copy.id === id
            ? { ...copy, cardStatus: { kind: "pending", message: "Making a copy…" } }
            : copy,
        ),
      );
    },
    finishCopy: (id: string, result: DrawingSaveResponse) => {
      unconfirmedCopyPeopleStatusRef.current.set(id, false);
      recordCopyMetadata(id, result);
      updateCopies((current) =>
        current.map((copy) =>
          copy.id === id
            ? {
                ...copy,
                cardStatus: undefined,
                copyRetryable: undefined,
                copySourceDrawingId: undefined,
                sharedWithPeople: false,
                title: result.name ?? copy.title,
                lastModifiedAt: result.updatedAt,
              }
            : copy,
        ),
      );
      setAiStatusKnownDrawingIds((current) => new Set(current).add(id));
      void fetchTab("recent");
    },
    failCopy: (
      id: string,
      message: string,
      retryable: boolean,
      refresh = false,
      result?: DrawingSaveResponse,
    ) => {
      if (result) recordCopyMetadata(id, result);
      updateCopies((current) =>
        current.map((copy) =>
          copy.id === id
            ? {
                ...copy,
                cardStatus: { kind: "error", message },
                copyRetryable: retryable,
              }
            : copy,
        ),
      );
      if (refresh) void fetchTab("recent");
    },
    dismissCopy: (id: string) => {
      updateCopies((current) => current.filter((copy) => copy.id !== id));
    },
    refreshOwned: () => {
      void fetchTab("recent");
    },
    retry: () => {
      const append =
        !retrySharingStatusRef.current.has(activeTab) &&
        !retryWithoutAppendRef.current.has(activeTab) &&
        itemsByTab[activeTab].length > 0 &&
        Boolean(continuationTokenByTabRef.current[activeTab]);
      void fetchTab(activeTab, true, false, append).then((hydrated) => {
        if (activeTab === "shared" && hydrated) {
          sharedHydratedRevisionRef.current = sharedIndexRevision;
        }
      });
    },
    recentCount: totalCountByTab.recent,
    sharedCount: totalCountByTab.shared,
    aiCount: totalCountByTab.ai,
    /** Drawing ids that currently carry an active AI grant, for the AI badge on
     *  the other tabs (design D9.4). */
    aiGrantedDrawingIds: new Set(grantMetadataByDrawingId.keys()),
    /** drawingId → its active AI grant, so the Share with AI dialog opens on the
     *  existing code instead of offering a duplicate mint (design D9). The
     *  access travels with the code so the dialog can state what an assistant
     *  will be able to do — a report, not a setting. */
    aiGrantsByDrawingId: grantMetadataByDrawingId,
    aiStatusKnownDrawingIds,
    setPeopleSharingStatus: (drawingId: string, isShared: boolean) => {
      if (unconfirmedCopyPeopleStatusRef.current.has(drawingId)) {
        unconfirmedCopyPeopleStatusRef.current.set(drawingId, isShared);
      }
      peopleMutationRef.current.set(drawingId, {
        revision: ++sharingRevisionRef.current,
        isShared,
      });
      const update = (tab: GalleryTabId) => (items: DrawingItem[]) => {
        const next = items.map((item) =>
          item.drawingId === drawingId ? { ...item, sharedWithPeople: isShared } : item,
        );
        writeCachedItems(cacheKey, tab, next);
        return next;
      };
      setOwnedItems(update("recent"));
      setSharedItems(update("shared"));
      setAiItems(update("ai"));
      if (copiesRef.current.some((copy) => copy.drawingId === drawingId && !copy.cardStatus)) {
        updateCopies((current) =>
          current.map((copy) =>
            copy.drawingId === drawingId ? { ...copy, sharedWithPeople: isShared } : copy,
          ),
        );
      }
    },
    /**
     * Apply a committed rename in place across every tab. The rename advances
     * the drawing's `updatedAt`, so the kept preview is re-stamped to it; the
     * next re-list then recognises the thumbnail as current instead of
     * downloading it again.
     */
    renameItem: (drawingId: string, name: string, updatedAt: string) => {
      const persistedCopy = persistedCopyMetadataRef.current.get(drawingId);
      if (persistedCopy) persistedCopyMetadataRef.current.set(drawingId, { name, updatedAt });
      (["recent", "shared", "ai"] as const).forEach((tab) => {
        if (previewStampsByTabRef.current[tab].has(drawingId)) {
          previewStampsByTabRef.current[tab].set(drawingId, updatedAt);
        }
        const listed = listedDrawingsByTabRef.current[tab];
        if (listed) {
          listedDrawingsByTabRef.current[tab] = listed.map((drawing) =>
            drawing.drawingId === drawingId ? { ...drawing, updatedAt } : drawing,
          );
        }
        const detail = detailsByTabRef.current[tab].get(drawingId);
        if (detail) detailsByTabRef.current[tab].set(drawingId, { ...detail, name });
      });
      const rename = (item: DrawingItem): DrawingItem =>
        item.drawingId === drawingId ? { ...item, title: name, lastModifiedAt: updatedAt } : item;
      const update = (tab: GalleryTabId) => (items: DrawingItem[]) => {
        if (!items.some((item) => item.drawingId === drawingId)) return items;
        const next = items.map(rename);
        writeCachedItems(cacheKey, tab, next);
        return next;
      };
      setOwnedItems(update("recent"));
      setSharedItems(update("shared"));
      setAiItems(update("ai"));
      if (copiesRef.current.some((copy) => copy.drawingId === drawingId)) {
        updateCopies((current) => current.map(rename));
      }
    },
    removeItem: (drawingId: string) => {
      deletedDrawingIdsRef.current.add(drawingId);
      persistedCopyMetadataRef.current.delete(drawingId);
      requestIdRef.current.recent += 1;
      listedDrawingsByTabRef.current.recent =
        listedDrawingsByTabRef.current.recent?.filter(
          (drawing) => drawing.drawingId !== drawingId,
        ) ?? null;
      detailsByTabRef.current.recent.delete(drawingId);
      deletePreview(drawingId);
      unconfirmedCopyPeopleStatusRef.current.delete(drawingId);
      const wasCopy = copiesRef.current.some((copy) => copy.drawingId === drawingId);
      if (wasCopy) {
        updateCopies((current) => current.filter((copy) => copy.drawingId !== drawingId));
      }
      const wasOwned = ownedItemsRef.current.some((item) => item.drawingId === drawingId);
      const wasShared = sharedItemsRef.current.some((item) => item.drawingId === drawingId);
      const wasAi = aiItemsRef.current.some((item) => item.drawingId === drawingId);
      if (wasOwned || wasShared || wasAi) {
        setTotalCountByTab((current) => {
          const next = {
            recent: Math.max(0, current.recent - (wasOwned ? 1 : 0)),
            shared: Math.max(0, current.shared - (wasShared ? 1 : 0)),
            ai: Math.max(0, current.ai - (wasAi ? 1 : 0)),
          };
          totalCountByTabRef.current = next;
          (["recent", "shared", "ai"] as const).forEach((tab) =>
            writeCachedPageState(cacheKey, tab, next[tab], continuationTokenByTabRef.current[tab]),
          );
          return next;
        });
      }
      (["recent", "shared", "ai"] as const).forEach((tab) => {
        const previewUrl = previewUrlsByTabRef.current[tab].get(drawingId);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          previewUrlsByTabRef.current[tab].delete(drawingId);
        }
      });
      setOwnedItems((items) => {
        const next = items.filter((item) => item.drawingId !== drawingId);
        writeCachedItems(cacheKey, "recent", next);
        return next;
      });
      setSharedItems((items) => {
        const next = items.filter((item) => item.drawingId !== drawingId);
        writeCachedItems(cacheKey, "shared", next);
        return next;
      });
      setAiItems((items) => {
        const next = items.filter((item) => item.drawingId !== drawingId);
        writeCachedItems(cacheKey, "ai", next);
        return next;
      });
      setGrantMetadataByDrawingId((current) => {
        const next = new Map(current);
        next.delete(drawingId);
        return next;
      });
      void fetchTab("recent");
    },
    /**
     * Record a freshly minted (or in-place updated) AI grant so the "Shared
     * with AI" tab, its count, and the AI badge/code on the drawing tabs stay
     * in sync after a mint from a gallery card (design D9). Idempotent by
     * drawingId: minting an already-shared drawing returns the same code, so we
     * replace the existing row rather than adding a duplicate.
     */
    addAiGrant: (item: DrawingItem, grant: AiGrant, access: AiGrantAccess) => {
      aiMutationRef.current.set(item.drawingId, {
        revision: ++sharingRevisionRef.current,
        grant: { code: grant.code, access, lastUsedAt: grant.lastUsedAt },
      });
      setAiStatusKnownDrawingIds((current) => new Set([...current, item.drawingId]));
      const updateProjection = (tab: GalleryTabId) => (items: DrawingItem[]) => {
        const next = items.map((existing) =>
          existing.drawingId === item.drawingId
            ? {
                ...existing,
                aiGrantCode: grant.code,
                aiGrantAccess: access,
                aiGrantLastUsedAt: grant.lastUsedAt,
                aiGrantMissingDrawing: false,
              }
            : existing,
        );
        writeCachedItems(cacheKey, tab, next);
        return next;
      };
      setOwnedItems(updateProjection("recent"));
      setSharedItems(updateProjection("shared"));
      const aiItem: DrawingItem = {
        ...item,
        sharedWithPeople:
          peopleMutationRef.current.get(item.drawingId)?.isShared ??
          ownedItemsRef.current.find((existing) => existing.drawingId === item.drawingId)
            ?.sharedWithPeople ??
          sharedItemsRef.current.find((existing) => existing.drawingId === item.drawingId)
            ?.sharedWithPeople ??
          item.sharedWithPeople,
        thumbnailUrl: undefined,
        aiGrantCode: grant.code,
        aiGrantAccess: access,
        aiGrantLastUsedAt: grant.lastUsedAt,
        aiGrantMissingDrawing: false,
      };
      setAiItems((items) => {
        const rest = items.filter((existing) => existing.drawingId !== item.drawingId);
        const next = [aiItem, ...rest];
        writeCachedItems(cacheKey, "ai", next);
        return next;
      });
      const existed =
        grantMetadataByDrawingId.has(item.drawingId) ||
        aiItemsRef.current.some((existing) => existing.drawingId === item.drawingId);
      setGrantMetadataByDrawingId((current) => {
        const next = new Map(current);
        next.set(item.drawingId, { code: grant.code, access });
        return next;
      });
      if (!existed) {
        setTotalCountByTab((counts) => {
          const ai = counts.ai + 1;
          writeCachedPageState(cacheKey, "ai", ai, continuationTokenByTabRef.current.ai);
          return { ...counts, ai };
        });
      }
    },
    setAiGrantStatus: (
      item: DrawingItem,
      grant: AiGrantListItem | null,
      access: AiGrantAccess | null,
    ) => {
      if (grant) {
        aiMutationRef.current.set(item.drawingId, {
          revision: ++sharingRevisionRef.current,
          grant: { code: grant.code, access, lastUsedAt: grant.lastUsedAt },
        });
        setAiStatusKnownDrawingIds((current) => new Set([...current, item.drawingId]));
        const updateProjection = (tab: GalleryTabId) => (items: DrawingItem[]) => {
          const next = items.map((existing) =>
            existing.drawingId === item.drawingId
              ? {
                  ...existing,
                  aiGrantCode: grant.code,
                  aiGrantAccess: access ?? undefined,
                  aiGrantLastUsedAt: grant.lastUsedAt,
                  aiGrantMissingDrawing: !grant.drawing,
                }
              : existing,
          );
          writeCachedItems(cacheKey, tab, next);
          return next;
        };
        setOwnedItems(updateProjection("recent"));
        setSharedItems(updateProjection("shared"));
        const aiItem: DrawingItem = {
          ...item,
          sharedWithPeople:
            peopleMutationRef.current.get(item.drawingId)?.isShared ??
            ownedItemsRef.current.find((existing) => existing.drawingId === item.drawingId)
              ?.sharedWithPeople ??
            sharedItemsRef.current.find((existing) => existing.drawingId === item.drawingId)
              ?.sharedWithPeople ??
            item.sharedWithPeople,
          thumbnailUrl: undefined,
          aiGrantCode: grant.code,
          aiGrantAccess: access ?? undefined,
          aiGrantLastUsedAt: grant.lastUsedAt,
          aiGrantMissingDrawing: !grant.drawing,
        };
        setAiItems((items) => {
          const rest = items.filter((existing) => existing.drawingId !== item.drawingId);
          const next = [aiItem, ...rest];
          writeCachedItems(cacheKey, "ai", next);
          return next;
        });
        const existed =
          grantMetadataByDrawingId.has(item.drawingId) ||
          aiItemsRef.current.some((existing) => existing.drawingId === item.drawingId);
        setGrantMetadataByDrawingId((current) => {
          const next = new Map(current);
          next.set(item.drawingId, { code: grant.code, access });
          return next;
        });
        if (!existed) {
          setTotalCountByTab((counts) => {
            const ai = counts.ai + 1;
            writeCachedPageState(cacheKey, "ai", ai, continuationTokenByTabRef.current.ai);
            return { ...counts, ai };
          });
        }
        return;
      }

      aiMutationRef.current.set(item.drawingId, {
        revision: ++sharingRevisionRef.current,
        grant: null,
      });
      setAiStatusKnownDrawingIds((current) => new Set([...current, item.drawingId]));
      const clearProjection = (tab: GalleryTabId) => (items: DrawingItem[]) => {
        const next = items.map((existing) =>
          existing.drawingId === item.drawingId
            ? {
                ...existing,
                aiGrantCode: undefined,
                aiGrantAccess: undefined,
                aiGrantLastUsedAt: undefined,
                aiGrantMissingDrawing: undefined,
              }
            : existing,
        );
        writeCachedItems(cacheKey, tab, next);
        return next;
      };
      setOwnedItems(clearProjection("recent"));
      setSharedItems(clearProjection("shared"));
      const existed =
        grantMetadataByDrawingId.has(item.drawingId) ||
        aiItems.some((existing) => existing.drawingId === item.drawingId);
      setAiItems((items) => {
        const removed = items.find((existing) => existing.drawingId === item.drawingId);
        if (removed) {
          const previewUrl = previewUrlsByTabRef.current.ai.get(item.drawingId);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            previewUrlsByTabRef.current.ai.delete(item.drawingId);
          }
        }
        const next = items.filter((existing) => existing.drawingId !== item.drawingId);
        writeCachedItems(cacheKey, "ai", next);
        return next;
      });
      setGrantMetadataByDrawingId((current) => {
        const next = new Map(current);
        next.delete(item.drawingId);
        return next;
      });
      if (existed) {
        setTotalCountByTab((counts) => {
          const ai = Math.max(0, counts.ai - 1);
          writeCachedPageState(cacheKey, "ai", ai, continuationTokenByTabRef.current.ai);
          return { ...counts, ai };
        });
      }
    },
    /**
     * Drop a revoked AI grant from the "Shared with AI" tab (design D9). The
     * drawing itself is untouched — only the grant row is removed — so the
     * other tabs keep the drawing.
     */
    removeAiGrant: (code: string) => {
      const metadataEntry = [...grantMetadataByDrawingId.entries()].find(
        ([, grant]) => grant.code === code,
      );
      const listedEntry = aiItems.find((item) => item.aiGrantCode === code);
      const drawingEntry =
        ownedItemsRef.current.find((item) => item.aiGrantCode === code) ??
        sharedItemsRef.current.find((item) => item.aiGrantCode === code);
      const knownId = metadataEntry?.[0] ?? listedEntry?.drawingId ?? drawingEntry?.drawingId;
      if (knownId) {
        aiMutationRef.current.set(knownId, {
          revision: ++sharingRevisionRef.current,
          grant: null,
        });
      }
      if (knownId) setAiStatusKnownDrawingIds((current) => new Set([...current, knownId]));
      if (knownId) {
        const clearProjection = (tab: GalleryTabId) => (items: DrawingItem[]) => {
          const next = items.map((item) =>
            item.drawingId === knownId
              ? {
                  ...item,
                  aiGrantCode: undefined,
                  aiGrantAccess: undefined,
                  aiGrantLastUsedAt: undefined,
                  aiGrantMissingDrawing: undefined,
                }
              : item,
          );
          writeCachedItems(cacheKey, tab, next);
          return next;
        };
        setOwnedItems(clearProjection("recent"));
        setSharedItems(clearProjection("shared"));
      }
      setAiItems((items) => {
        const removed = items.find((item) => item.aiGrantCode === code);
        if (removed) {
          const previewUrl = previewUrlsByTabRef.current.ai.get(removed.drawingId);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            previewUrlsByTabRef.current.ai.delete(removed.drawingId);
          }
        }
        const next = items.filter((item) => item.aiGrantCode !== code);
        writeCachedItems(cacheKey, "ai", next);
        return next;
      });
      setGrantMetadataByDrawingId((current) => {
        const drawingId = metadataEntry?.[0] ?? listedEntry?.drawingId;
        if (!drawingId) return current;
        const next = new Map(current);
        next.delete(drawingId);
        return next;
      });
      if (metadataEntry || listedEntry) {
        setTotalCountByTab((counts) => {
          const ai = Math.max(0, counts.ai - 1);
          writeCachedPageState(cacheKey, "ai", ai, continuationTokenByTabRef.current.ai);
          return { ...counts, ai };
        });
      }
    },
  };
}
